import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { BarChart3, TrendingUp, DollarSign, Percent, Calculator, Building } from 'lucide-react';
import { safeJson } from '../../utils/api';

const FundamentalAnalysis = () => {
  const [fundamentalData, setFundamentalData] = useState(null);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState('AAPL');

  const TWELVE_DATA_API_KEY = process.env.REACT_APP_TWELVE_DATA_API_KEY || 'demo';

  // Получение фундаментальных данных
  const fetchFundamentalData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const symbol = getSymbolForAsset(selectedAsset);
      
      // Проверяем, является ли актив акцией (для акций доступны фундаментальные данные)
      if (!isStock(selectedAsset)) {
        setFundamentalData(getFallbackDataForNonStock(selectedAsset));
        return;
      }

      console.log(`Fetching fundamental data for ${symbol}...`);

      // Запросы к Twelve Data API для фундаментальных данных
      const endpoints = [
        `https://api.twelvedata.com/profile?symbol=${symbol}&apikey=${TWELVE_DATA_API_KEY}`,
        `https://api.twelvedata.com/statistics?symbol=${symbol}&apikey=${TWELVE_DATA_API_KEY}`,
        `https://api.twelvedata.com/income_statement?symbol=${symbol}&apikey=${TWELVE_DATA_API_KEY}`,
        `https://api.twelvedata.com/balance_sheet?symbol=${symbol}&apikey=${TWELVE_DATA_API_KEY}`
      ];

      const promises = endpoints.map(url => 
        fetch(url)
          .then(response => safeJson(response))
          .catch(error => ({ error: error.message }))
      );

      const [profileData, statisticsData, incomeData, balanceData] = await Promise.all(promises);

      // Объединяем полученные данные
      const combinedData = {
        profile: profileData,
        statistics: statisticsData,
        income: incomeData,
        balance: balanceData,
        symbol: symbol,
        asset: selectedAsset
      };

      setFundamentalData(combinedData);
      console.log('Fundamental data loaded:', combinedData);

    } catch (error) {
      console.error('Error fetching fundamental data:', error);
      setError(error.message);
      // Используем fallback данные
      setFundamentalData(getFallbackFundamentalData(selectedAsset));
    } finally {
      setLoading(false);
    }
  };

  // Функция получения символа
  const getSymbolForAsset = (asset) => {
    const symbolMapping = {
      'AAPL': 'AAPL',
      'GOOGL': 'GOOGL', 
      'TSLA': 'TSLA',
      'MSFT': 'MSFT',
      'AMZN': 'AMZN',
      'NFLX': 'NFLX',
      'NVDA': 'NVDA',
      'META': 'META'
    };
    return symbolMapping[asset] || asset;
  };

  // Проверка, является ли актив акцией
  const isStock = (asset) => {
    const stocks = ['AAPL', 'GOOGL', 'TSLA', 'MSFT', 'AMZN', 'NFLX', 'NVDA', 'META'];
    return stocks.includes(asset);
  };

  // Fallback данные для не-акций (форекс, крипто)
  const getFallbackDataForNonStock = (asset) => {
    return {
      type: 'non-stock',
      asset: asset,
      message: asset.includes('USD') ? 
        'Фундаментальный анализ доступен только для акций. Для валютных пар рекомендуется анализ макроэкономических показателей.' :
        'Фундаментальный анализ доступен только для акций. Для криптовалют рекомендуется анализ on-chain метрик.'
    };
  };

  // Fallback данные для демонстрации (акции)
  const getFallbackFundamentalData = (asset) => {
    const symbol = getSymbolForAsset(asset);
    
    const mockData = {
      'AAPL': {
        name: 'Apple Inc.',
        sector: 'Technology',
        marketCap: '2.89T',
        pe: 28.5,
        pb: 10.2,
        roe: 56.8,
        roa: 22.4,
        debtToEquity: 1.73,
        currentRatio: 0.98,
        quickRatio: 0.81,
        grossMargin: 44.1,
        operatingMargin: 30.7,
        netMargin: 25.3,
        revenue: '383.29B',
        netIncome: '97.00B',
        eps: 6.16,
        dividendYield: 0.50,
        beta: 1.29
      },
      'MSFT': {
        name: 'Microsoft Corporation',
        sector: 'Technology', 
        marketCap: '2.78T',
        pe: 32.1,
        pb: 12.8,
        roe: 36.2,
        roa: 18.1,
        debtToEquity: 0.47,
        currentRatio: 1.77,
        quickRatio: 1.76,
        grossMargin: 68.4,
        operatingMargin: 42.0,
        netMargin: 34.1,
        revenue: '211.92B',
        netIncome: '72.36B',
        eps: 9.65,
        dividendYield: 0.72,
        beta: 0.90
      },
      'NVDA': {
        name: 'NVIDIA Corporation',
        sector: 'Technology',
        marketCap: '1.75T',
        pe: 65.2,
        pb: 22.4,
        roe: 54.2,
        roa: 35.8,
        debtToEquity: 0.24,
        currentRatio: 3.42,
        quickRatio: 3.12,
        grossMargin: 73.2,
        operatingMargin: 45.6,
        netMargin: 36.7,
        revenue: '60.92B',
        netIncome: '22.36B',
        eps: 9.12,
        dividendYield: 0.09,
        beta: 1.68
      },
      'GOOGL': {
        name: 'Alphabet Inc.',
        sector: 'Technology',
        marketCap: '1.65T',
        pe: 24.8,
        pb: 5.2,
        roe: 21.2,
        roa: 14.8,
        debtToEquity: 0.11,
        currentRatio: 2.63,
        quickRatio: 2.58,
        grossMargin: 57.3,
        operatingMargin: 28.4,
        netMargin: 23.1,
        revenue: '282.84B',
        netIncome: '73.75B',
        eps: 5.80,
        dividendYield: 0.00,
        beta: 1.05
      },
      'TSLA': {
        name: 'Tesla Inc.',
        sector: 'Automotive',
        marketCap: '785B',
        pe: 44.3,
        pb: 8.9,
        roe: 20.4,
        roa: 11.2,
        debtToEquity: 0.17,
        currentRatio: 1.29,
        quickRatio: 0.92,
        grossMargin: 19.7,
        operatingMargin: 8.1,
        netMargin: 12.3,
        revenue: '96.77B',
        netIncome: '15.00B',
        eps: 4.73,
        dividendYield: 0.00,
        beta: 2.31
      }
    };

    const data = mockData[symbol] || mockData['AAPL'];
    
    return {
      type: 'stock',
      symbol: symbol,
      asset: asset,
      profile: {
        name: data.name,
        sector: data.sector,
        market_capitalization: data.marketCap
      },
      ratios: {
        pe: data.pe,
        pb: data.pb,
        roe: data.roe,
        roa: data.roa,
        debt_to_equity: data.debtToEquity,
        current_ratio: data.currentRatio,
        quick_ratio: data.quickRatio,
        beta: data.beta
      },
      margins: {
        gross_margin: data.grossMargin,
        operating_margin: data.operatingMargin,
        net_margin: data.netMargin
      },
      financials: {
        revenue: data.revenue,
        net_income: data.netIncome,
        eps: data.eps,
        dividend_yield: data.dividendYield
      }
    };
  };

  // Компонент карточки метрики - УМЕНЬШЕННЫЙ РАЗМЕР
  const MetricCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
    <div className="bg-gray-700 rounded-lg p-2 border border-gray-600">
      <div className="flex items-center justify-between mb-1">
        <div className={`p-1 rounded-full ${color}`}>
          <Icon size={14} className="text-white" />
        </div>
        {trend && (
          <div className={`text-xs px-1 py-0.5 rounded-full ${
            trend > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
      <h4 className="text-gray-300 text-xs font-medium mb-0.5">{title}</h4>
      <p className="text-white text-sm font-bold">{value}</p>
      {subtitle && <p className="text-gray-400 text-xs mt-0.5">{subtitle}</p>}
    </div>
  );

  useEffect(() => {
    fetchFundamentalData();
  }, [selectedAsset]);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
          <span className="ml-3 text-gray-400">{tt('Загрузка фундаментальных данных...')}</span>
        </div>
      </div>
    );
  }

  if (fundamentalData?.type === 'non-stock') {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white text-lg font-semibold mb-4 flex items-center">
          <BarChart3 className="mr-2" size={20} />
          Фундаментальный анализ
        </h3>
        <div className="text-center py-8">
          <Building size={48} className="mx-auto mb-4 text-gray-500" />
          <p className="text-gray-400 text-sm">{fundamentalData.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white text-lg font-semibold flex items-center">
          <BarChart3 className="mr-2" size={20} />
          Фундаментальный анализ
        </h3>
        
        <div className="flex items-center space-x-2 lg:space-x-4 flex-wrap lg:flex-nowrap">
          {/* Asset Selector - только акции */}
          <select
            value={selectedAsset}
            onChange={(e) => setSelectedAsset(e.target.value)}
            className="bg-gray-700 text-white text-sm rounded px-3 py-2 border border-gray-600 min-w-[150px]"
          >
            <optgroup label="Акции USA">
              <option value="AAPL">Apple Inc (AAPL)</option>
              <option value="GOOGL">Alphabet Inc (GOOGL)</option>
              <option value="TSLA">Tesla Inc (TSLA)</option>
              <option value="MSFT">Microsoft (MSFT)</option>
              <option value="AMZN">Amazon (AMZN)</option>
              <option value="NFLX">Netflix (NFLX)</option>
              <option value="NVDA">NVIDIA (NVDA)</option>
              <option value="META">Meta (META)</option>
              <option value="AMD">AMD (AMD)</option>
              <option value="INTC">Intel (INTC)</option>
              <option value="CRM">Salesforce (CRM)</option>
              <option value="ORCL">Oracle (ORCL)</option>
              <option value="IBM">IBM (IBM)</option>
              <option value="CSCO">Cisco (CSCO)</option>
              <option value="ADBE">Adobe (ADBE)</option>
            </optgroup>
          </select>
          
          <div className="text-right">
            <p className="text-white font-medium">{fundamentalData?.profile?.name || fundamentalData?.symbol}</p>
            <p className="text-gray-400 text-sm">{fundamentalData?.profile?.sector || 'Sector N/A'}</p>
          </div>
        </div>
      </div>

      {fundamentalData && (
        <div className="space-y-3">
          {/* Ключевые коэффициенты */}
          <div>
            <h4 className="text-white text-sm font-medium mb-2">{tt('Ключевые коэффициенты')}</h4>
            <div className="grid grid-cols-4 gap-2">
              <MetricCard
                title="P/E Ratio"
                value={fundamentalData.ratios?.pe || 'N/A'}
                icon={Calculator}
                color="bg-blue-500"
                subtitle={tt('Цена/Прибыль')}
              />
              <MetricCard
                title="P/B Ratio"
                value={fundamentalData.ratios?.pb || 'N/A'}
                icon={TrendingUp}
                color="bg-purple-500"
                subtitle={tt('Цена/Балансовая стоимость')}
              />
              <MetricCard
                title="ROE"
                value={`${fundamentalData.ratios?.roe || 0}%`}
                icon={Percent}
                color="bg-green-500"
                subtitle={tt('Рентабельность собственного капитала')}
              />
              <MetricCard
                title="Debt/Equity"
                value={fundamentalData.ratios?.debt_to_equity || 'N/A'}
                icon={Building}
                color="bg-orange-500"
                subtitle={tt('Долг/Собственный капитал')}
              />
            </div>
          </div>

          {/* Рентабельность */}
          <div>
            <h4 className="text-white text-sm font-medium mb-2">{tt('Маржинальность')}</h4>
            <div className="grid grid-cols-3 gap-2">
              <MetricCard
                title={tt('Валовая маржа')}
                value={`${fundamentalData.margins?.gross_margin || 0}%`}
                icon={DollarSign}
                color="bg-emerald-500"
              />
              <MetricCard
                title={tt('Операционная маржа')}
                value={`${fundamentalData.margins?.operating_margin || 0}%`}
                icon={TrendingUp}
                color="bg-cyan-500"
              />
              <MetricCard
                title={tt('Чистая маржа')}
                value={`${fundamentalData.margins?.net_margin || 0}%`}
                icon={Calculator}
                color="bg-indigo-500"
              />
            </div>
          </div>

          {/* Финансовые показатели */}
          <div>
            <h4 className="text-white text-sm font-medium mb-2">{tt('Финансовые показатели')}</h4>
            <div className="grid grid-cols-4 gap-2">
              <MetricCard
                title={tt('Выручка')}
                value={fundamentalData.financials?.revenue || 'N/A'}
                icon={DollarSign}
                color="bg-green-600"
                subtitle={tt('Общая выручка')}
              />
              <MetricCard
                title={tt('Чистая прибыль')}
                value={fundamentalData.financials?.net_income || 'N/A'}
                icon={TrendingUp}
                color="bg-emerald-600"
                subtitle={tt('Чистая прибыль')}
              />
              <MetricCard
                title="EPS"
                value={`$${fundamentalData.financials?.eps || 0}`}
                icon={Calculator}
                color="bg-blue-600"
                subtitle={tt('Прибыль на акцию')}
              />
              <MetricCard
                title={tt('Дивидендная доходность')}
                value={`${fundamentalData.financials?.dividend_yield || 0}%`}
                icon={Percent}
                color="bg-purple-600"
                subtitle={tt('Годовая доходность')}
              />
            </div>
          </div>

          {/* Ликвидность и риск */}
          <div>
            <h4 className="text-white text-sm font-medium mb-2">{tt('Ликвидность и риск')}</h4>
            <div className="grid grid-cols-3 gap-2">
              <MetricCard
                title={tt('Коэффициент текущей ликвидности')}
                value={fundamentalData.ratios?.current_ratio || 'N/A'}
                icon={Building}
                color="bg-yellow-500"
                subtitle="Current Ratio"
              />
              <MetricCard
                title={tt('Быстрая ликвидность')}
                value={fundamentalData.ratios?.quick_ratio || 'N/A'}
                icon={TrendingUp}
                color="bg-red-500"
                subtitle="Quick Ratio"
              />
              <MetricCard
                title="Beta"
                value={fundamentalData.ratios?.beta || 'N/A'}
                icon={Calculator}
                color="bg-gray-500"
                subtitle={tt('Коэффициент бета')}
              />
            </div>
          </div>

          {error && (
            <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg p-3">
              <p className="text-yellow-300 text-sm">
                ⚠️ Данные загружены из демо-источника. Для получения реальных данных требуется активная подписка на Twelve Data API.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FundamentalAnalysis;